import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Calendar, User, Clock, Share2 } from "lucide-react";
import { PageHeader } from "@/components/layout/Navigation";
import api from "@/lib/api";

const BlogPostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getBlogPost(slug)
      .then(res => {
        setPost(res.data);
        // Fetch related posts
        return api.getBlogPosts(res.data.category, 4);
      })
      .then(res => {
        setRelatedPosts(res.data.filter(p => p.slug !== slug).slice(0, 3));
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setLoading(false);
      });
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#1B4D3E] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <h1 className="font-display text-4xl text-[#1A1A1A] mb-4">Post Not Found</h1>
        <Link to="/blog" className="btn-primary">View All Posts</Link>
      </div>
    );
  }

  return (
    <div data-testid="blog-post-page">
      <PageHeader 
        title={post.title}
        breadcrumbs={[
          { name: "Blog", href: "/blog" },
          { name: post.category }
        ]}
      />

      <article className="py-20 md:py-32 bg-[#F9F9F7]">
        <div className="max-w-[900px] mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* Meta */}
            <div className="flex flex-wrap items-center gap-6 mb-8 text-sm text-[#4A4A4A]">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-[#1B4D3E]" />
                <span>{post.author}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-[#1B4D3E]" />
                <span>{new Date(post.created_at).toLocaleDateString('en-US', { 
                  month: 'long', 
                  day: 'numeric', 
                  year: 'numeric' 
                })}</span>
              </div>
              <span className="px-3 py-1 bg-[#1B4D3E]/10 text-[#1B4D3E] rounded-full text-xs font-medium">
                {post.category}
              </span>
            </div>

            {/* Featured Image */}
            <div className="rounded-3xl overflow-hidden mb-12">
              <img 
                src={post.image_url}
                alt={post.title}
                className="w-full h-auto"
              />
            </div>

            {/* Content */}
            <div className="prose prose-lg max-w-none">
              <p className="text-xl text-[#4A4A4A] font-light leading-relaxed mb-8">
                {post.excerpt}
              </p>
              <div className="text-[#4A4A4A] leading-relaxed whitespace-pre-line">
                {post.content}
              </div>
            </div>

            {/* Tags */}
            {post.tags && post.tags.length > 0 && (
              <div className="mt-12 pt-8 border-t border-[#1B4D3E]/10">
                <div className="flex flex-wrap gap-2">
                  {post.tags.map(tag => (
                    <span 
                      key={tag}
                      className="px-4 py-2 bg-white border border-[#1B4D3E]/10 rounded-full text-sm text-[#4A4A4A]"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Back Link */}
            <div className="mt-12">
              <Link to="/blog" className="inline-flex items-center gap-2 text-[#1B4D3E] font-medium hover:gap-3 transition-all">
                <ArrowLeft className="w-4 h-4" />
                Back to Blog
              </Link>
            </div>
          </motion.div>
        </div>
      </article>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="py-20 bg-white">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12">
            <h2 className="font-display text-3xl text-[#1A1A1A] mb-12">Related Articles</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {relatedPosts.map(relatedPost => (
                <Link 
                  key={relatedPost.id}
                  to={`/blog/${relatedPost.slug}`}
                  className="group"
                >
                  <div className="aspect-[16/10] rounded-2xl overflow-hidden mb-4">
                    <img 
                      src={relatedPost.image_url}
                      alt={relatedPost.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <h3 className="font-display text-lg text-[#1A1A1A] group-hover:text-[#1B4D3E] transition-colors">
                    {relatedPost.title}
                  </h3>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default BlogPostPage;
